import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { combineLatest, forkJoin, of, catchError, filter, map, switchMap, take } from 'rxjs';
import { SwiperOptions } from 'swiper/types';

import { CmsComponent, PageMeta, PageMetaService } from '@spartacus/core';

import {
  BrandsEnum, IconSizeEnum, IExtendedLink, ILinkMedia, IPushCardWithLayout, PositionEnum, SizesEnum
} from '@seb/web-design-system/core';

import { SdPushSliderComponent } from '@seb/web-design-system/push-slider';

import { HobMapper, LinkMapper, PushCardMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { CategoryService, ComponentService, HobStateService, TrackingService } from '../../../services';
import { WrapperComponent } from '../../static/core/wrapper/wrapper.component';
import { UppercaseBrandThemesEnum, UppercaseThemesEnum } from '../../../enums';
import { AbstractComponent } from '../abstract-component.component';

import {
  CategoryPushHobComponentInterface, PushCardImageRatioEnum, PushCardMediaConfigEnum,
  PushCardSizeEnum, PushCardTextConfigEnum, SliderHobComponentInterface, Wrapper
} from '../../../models';

@Component({
  selector: 'wc-slider-hob',
  templateUrl: './slider-hob.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdPushSliderComponent
  ]
})
export class SliderHobComponent extends AbstractComponent<SliderHobComponentInterface> implements OnInit {
  titleLevelHeading: number;
  wrapper: Wrapper;
  sizesEnum = SizesEnum;
  pushs: IPushCardWithLayout[] = [];
  context: string;
  isReady = false;
  isHob = false;

  pushSliderConfig: SwiperOptions = {
    pagination: {
      dynamicBullets: false,
      type: 'bullets',
      clickable: true
    },
    breakpoints: {
      0: {
        spaceBetween: 20,
        slidesPerView: 'auto'
      },
      768: {
        spaceBetween: 20
      },
      1280: {
        spaceBetween: 20,
        slidesPerView: 4
      }
    }
  };

  constructor(
    private readonly componentService: ComponentService,
    private readonly pageMetaService: PageMetaService,
    private readonly pushCardMapper: PushCardMapper,
    private readonly hobMapper: HobMapper,
    private readonly trackingService: TrackingService,
    private readonly categoryService: CategoryService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly hobStateService: HobStateService,
    private readonly linkMapper: LinkMapper,
  ) {
    super();
  }

  override mandatoryFunction = (cmp: SliderHobComponentInterface): boolean => {
    return !!cmp.components && cmp.components.length > 0;
  };

  ngOnInit() {
    this.isHob = this.hobStateService.hobState.isHob;

    this.subscriptions.add(
      combineLatest([this.component$, this.pageMetaService.getMeta()]).pipe(
        switchMap(([cmp, pageMetaData]) => {
          const component = cmp?.components?.length > 0
            ? this.componentService.getLazyComponentData(cmp as CmsComponent, 'components').pipe(
              catchError(() => {
                return of(cmp);
              })
            )
            : of(cmp);

          return combineLatest([component, of(pageMetaData)]);
        }),
        switchMap(([cmp, pageMetaData]: [SliderHobComponentInterface, PageMeta]) => {
          const categoryRequests$ = cmp.componentsObjects.map((component: CategoryPushHobComponentInterface) =>
            this.categoryService.getCategory(component?.category, false).pipe(
              filter((category) => category !== undefined),
              take(1),
              map((cat) => {
                component.categoryData = cat as CategoryPushHobComponentInterface;
                return component;
              }),
              catchError(() => {
                component.categoryData = null;
                return of(component);
              })
            )
          );
          return forkJoin(categoryRequests$).pipe(
            map(() => [cmp, pageMetaData])
          );
        }),
        take(1),
      ).subscribe(([components, pageMetaData]: [SliderHobComponentInterface, PageMeta]) => {
        this.context = this.wrapper?.wrapperHeading?.headline
          ? `${pageMetaData.title} ${this.wrapper?.wrapperHeading?.headline}`
          : pageMetaData.title;

        this.wrapper = {
          wrapperHeading: {
            headline: components?.headline,
            headlineLevel: components?.headlineLevel
          },
          wrapperBackgroundTheme: components?.backgroundTheme === UppercaseThemesEnum.LIGHT
            ? UppercaseThemesEnum.WHITE
            : UppercaseBrandThemesEnum.BRAND_DARK
        };

        this.titleLevelHeading = this.wrapper?.wrapperHeading?.headlineLevel
          ? transformHeadlineLevelToTitleLevel(this.wrapper?.wrapperHeading?.headlineLevel)
          : 2;

        const categoryPushHobComponents = components?.componentsObjects;

        categoryPushHobComponents.forEach((cmp: CategoryPushHobComponentInterface) => {
          const _push = cmp ? {
            heading: {
              headline: cmp.title || cmp.categoryData?.name,
              brandLogo: {
                media: this.hobMapper.getBrandLogoTitle(cmp.brand),
                position: PositionEnum.top,
                size: IconSizeEnum.small
              }
            },
            linksObjects: [
              {
                url: this.linkMapper.getBrandUrl(cmp.brand, (cmp.categoryUrl || cmp.categoryData?.url || '')) }
            ],
            brandBar: cmp.brand as BrandsEnum,
            media: cmp.media ? cmp.media : cmp.categoryData?.image,
            imageRatio: PushCardImageRatioEnum.R4_3,
            mediaConfig: PushCardMediaConfigEnum.TOP,
            textConfig: PushCardTextConfigEnum.CENTER,
            pushSize: PushCardSizeEnum.S
          } : {};
          this.pushs.push(this.pushCardMapper.toPushCardWithLayout(_push));
        });
        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  onClickLink(event: { link: IExtendedLink | ILinkMedia, isBrandCorner: boolean }): void {
    const eventType = this.isHob ? 'clickCrossBrand' : 'clickBrandCorner';
    this.trackingService.sendEvent(eventType, {
      productCategory: event?.link.url,
      positionLabel: 'section discover all our product range'
    });
  }
}
